import React, { useState } from "react";
import { useToast } from "../../context/ToastContext";
import {
  compactJobStatus,
  displayCountry,
  displayCustomer,
  displayOwnerSe,
  formatUpdatedShort,
  LIBRARY_ROW_GRID,
  sourceBasename,
} from "./rfpUtils";
import { downloadComplianceExcel, downloadOriginalRfp, errorDetail } from "./libraryDownloads";

function hasOriginal(job) {
  const artifacts = job?.artifacts || {};
  return Boolean(artifacts.pdf || artifacts.docx || artifacts.xlsx || artifacts.xls);
}

export default function RfpJobRow({ job, active, onOpen, dense = false }) {
  const toast = useToast();
  const [downloading, setDownloading] = useState("");
  const customer = displayCustomer(job);
  const country = displayCountry(job);
  const owner = displayOwnerSe(job);
  const updated = formatUpdatedShort(job.updated_at || job.created_at);
  const status = compactJobStatus(job);
  const original = hasOriginal(job);
  const compliance = Boolean(job?.artifacts?.compliance);

  const statusCls =
    status.kind === "failed"
      ? "text-red-600 dark:text-red-400"
      : status.kind === "processing"
        ? "font-medium text-amber-700 dark:text-amber-300"
        : status.kind === "review"
          ? "font-medium text-sky-700 dark:text-sky-300"
          : "text-emerald-700 dark:text-emerald-400";

  const download = async (event, kind) => {
    event.stopPropagation();
    if (downloading) return;
    setDownloading(kind);
    const base = sourceBasename(job?.filename, job?.job_id);
    try {
      if (kind === "source") {
        await downloadOriginalRfp(job.job_id, job?.filename || `${base}.pdf`);
        toast.success("Original RFP downloaded.");
      } else {
        await downloadComplianceExcel(job.job_id, `${base}_compliance.xlsx`);
        toast.success("Compliance Excel downloaded.");
      }
    } catch (err) {
      const fallback = kind === "source" ? "Original RFP download failed" : "Compliance download failed";
      toast.error(await errorDetail(err, fallback));
    } finally {
      setDownloading("");
    }
  };

  const actionBtn =
    "rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-white disabled:opacity-60 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(job.job_id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(job.job_id);
        }
      }}
      className={`w-full rounded-lg px-2 text-left transition ${LIBRARY_ROW_GRID} ${
        dense ? "min-h-8 py-1" : "min-h-9 py-1.5"
      } ${
        active
          ? "bg-emerald-50 ring-1 ring-emerald-500/40 dark:bg-emerald-950/30"
          : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
      }`}
      data-testid="rfp-job-row"
    >
      <span className="hidden truncate text-sm font-semibold text-slate-900 dark:text-slate-100 sm:block">{customer}</span>
      <span className="hidden truncate text-xs text-slate-500 dark:text-slate-400 sm:block">{country}</span>
      <span className="hidden truncate text-xs text-slate-500 dark:text-slate-400 sm:block">{owner}</span>
      <span className="hidden truncate text-xs text-slate-400 dark:text-slate-500 sm:block">{updated}</span>
      <span className={`hidden justify-self-end text-xs sm:block ${statusCls}`} title={status.kind === "done" ? "Completed" : status.label}>
        {status.label}
      </span>
      <span className="flex flex-col gap-0.5 sm:hidden">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{customer}</span>
          <span className={`shrink-0 text-xs ${statusCls}`}>{status.label}</span>
        </span>
        <span className="truncate text-xs text-slate-500 dark:text-slate-400">
          {country} · {owner} · {updated}
        </span>
      </span>
      <span className="mt-1 flex flex-wrap justify-end gap-1 sm:mt-0" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        {original ? (
          <button
            type="button"
            className={actionBtn}
            disabled={Boolean(downloading)}
            onClick={(event) => download(event, "source")}
            data-testid="rfp-download-source"
            aria-label="Download original RFP"
          >
            Original
          </button>
        ) : null}
        {compliance ? (
          <button
            type="button"
            className={actionBtn}
            disabled={Boolean(downloading)}
            onClick={(event) => download(event, "compliance")}
            data-testid="rfp-download-compliance"
            aria-label="Download compliance Excel"
          >
            Compliance
          </button>
        ) : null}
      </span>
    </div>
  );
}
